import { useMemo } from 'react';
import { Redirect, useLocation, generatePath } from 'quartz-routing/react-router';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useTicketThread } from '../data/useTicketThread';
import { useTicketThreads } from '../data/useTicketThreads';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { ATTACHMENTS_PANEL_OVERLAY_BREAKPOINT } from '../constants';
import { ROUTES } from '../constants/routes';
import { AttachmentsPanel } from './AttachmentsPanel';
import { LegacyThreadHistory } from './LegacyThreadHistory';
import { MessageComposer } from './MessageComposer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function LegacyTicketThreadViewerWrapper({
  setIsInfoPanelOpen,
  isAttachmentsPanelOpen,
  setIsAttachmentsPanelOpen
}) {
  var _ticketThread$data, _ticketThread$data2, _ticketThread$data3, _ticketThread$data4;
  const {
    ticketId,
    threadId
  } = useTicketDetailsParams();
  const location = useLocation();
  const isAtAttachmentsBreakpoint = useBreakpoint(ATTACHMENTS_PANEL_OVERLAY_BREAKPOINT);
  const isCreatingThread = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('creatingThread') === '1';
  }, [location.search]);
  const {
    threads,
    latestThread,
    loading: threadsLoading,
    error: threadsError
  } = useTicketThreads(ticketId, {
    shouldPoll: isCreatingThread && !threadId
  });
  const ticketThread = useTicketThread(ticketId, threadId);
  const handleTryAgain = () => {
    // POLISH: Would like to have different handlers here ideally.
    // If threads fails and we dont have a thread, then we should offer refresh as this does now.
    // But threads succeeds, and we have a thread, then we should offer a refetch of the new thread.
    // Refreshing wipes cache -- if only one of the thread fetches fails, we should not wipe cache and handle
    // that failure more gracefully.

    window.location.reload();
  };
  if (!threadId && latestThread && !threadsLoading) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: {
        pathname: generatePath(ROUTES.ticket, {
          ticketId,
          threadId: latestThread.threadId
        }),
        search: ''
      }
    });
  }
  if ((threadsError || ticketThread.error) && !ticketThread.data) {
    return /*#__PURE__*/_jsx(Flex, {
      flex: "1",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      gap: "400",
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: handleTryAgain,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.ticketInfoPanel.tryAgain"
        })
      })
    });
  }
  const isLoading = threadsLoading || ticketThread.loading;
  const messages = ((_ticketThread$data = ticketThread.data) === null || _ticketThread$data === void 0 || (_ticketThread$data = _ticketThread$data.pagedThreadHistory) === null || _ticketThread$data === void 0 ? void 0 : _ticketThread$data.results.filter(message => Boolean(message.sender)).sort((a, b) => a.timestamp - b.timestamp)) || [];
  const responders = ((_ticketThread$data2 = ticketThread.data) === null || _ticketThread$data2 === void 0 ? void 0 : _ticketThread$data2.responders) || {};
  const attachmentsMap = ((_ticketThread$data3 = ticketThread.data) === null || _ticketThread$data3 === void 0 ? void 0 : _ticketThread$data3.attachmentsMap) || {};
  const sensitiveAttachmentsMetadata = ((_ticketThread$data4 = ticketThread.data) === null || _ticketThread$data4 === void 0 ? void 0 : _ticketThread$data4.sensitiveAttachmentsMetadata) || {};
  return /*#__PURE__*/_jsxs(Flex, {
    flex: "1",
    flexDirection: "row",
    minInlineSize: "0",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flex: "1",
      flexDirection: "column",
      minInlineSize: "300px",
      children: [/*#__PURE__*/_jsx(Flex, {
        flex: "1",
        overflow: "hidden",
        minInlineSize: "100%",
        children: /*#__PURE__*/_jsx(LegacyThreadHistory, {
          threads: threads,
          isLoading: isLoading,
          isCreatingThread: isCreatingThread,
          hasMore: ticketThread.hasMore,
          fetchMore: ticketThread.fetchMore,
          messages: messages,
          isFetchingMore: ticketThread.loading,
          responders: responders,
          attachmentsMap: attachmentsMap,
          sensitiveAttachmentsMetadata: sensitiveAttachmentsMetadata,
          setIsInfoPanelOpen: setIsInfoPanelOpen,
          setIsAttachmentsPanelOpen: setIsAttachmentsPanelOpen,
          showAttachmentsButton: isAtAttachmentsBreakpoint
        })
      }), /*#__PURE__*/_jsx(Flex, {
        blockSize: "200px",
        flexShrink: "0",
        borderBlockStartWidth: "100",
        borderBlockStartStyle: "solid",
        borderColor: "border-core-disabled",
        children: /*#__PURE__*/_jsx(MessageComposer, {
          isLoading: isLoading || isCreatingThread
        })
      })]
    }), /*#__PURE__*/_jsx(AttachmentsPanel, {
      showAsOverlay: isAtAttachmentsBreakpoint,
      isAttachmentsPanelOpen: isAttachmentsPanelOpen,
      setIsAttachmentsPanelOpen: setIsAttachmentsPanelOpen
    })]
  });
}